import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { isValidProjectId } from '../../utils/projectIds';
import { publicStorefrontPath, storefrontToPayload } from '../../utils/storefront';
import StorefrontPreview from '../Storefront/StorefrontPreview';
import './StorefrontStudioPage.css';

const THEME_OPTIONS = ['editorial', 'cinematic', 'minimal', 'bold'];
const GENERATE_CREDIT_ESTIMATE = 2;
const REVISE_CREDIT_ESTIMATE = 1;
const COVER_PACKAGE_CREDIT_ESTIMATE = 9;

export default function StorefrontStudioPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;
  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const storefront = useQuery(api.storefronts.getByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const generateStorefront = useAction(api.storefronts.generateStorefront);
  const reviseStorefront = useAction(api.storefronts.reviseStorefront);
  const generateCoverPackage = useAction(api.storefronts.generateCoverPackage);
  const saveDraft = useMutation(api.storefronts.saveDraft);
  const publish = useMutation(api.storefronts.publish);
  const unpublish = useMutation(api.storefronts.unpublish);
  const ensureCredits = useMutation(api.credits.ensureForUser);
  const creditStatus = useQuery(api.credits.getStatus);

  const [draftPayload, setDraftPayload] = useState(null);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const payload = useMemo(() => {
    if (draftPayload) return draftPayload;
    return storefrontToPayload(storefront, project?.title || 'Untitled');
  }, [draftPayload, project?.title, storefront]);

  const publicPath = publicStorefrontPath(storefront);
  const publicUrl = publicPath ? `${window.location.origin}${publicPath}` : '';
  const totalCredits = creditStatus?.totalRemaining ?? 0;
  const hasGenerateCredits = creditStatus === undefined || totalCredits >= GENERATE_CREDIT_ESTIMATE;
  const hasReviseCredits = creditStatus === undefined || totalCredits >= REVISE_CREDIT_ESTIMATE;
  const hasCoverPackageCredits = creditStatus === undefined || totalCredits >= COVER_PACKAGE_CREDIT_ESTIMATE;

  useEffect(() => {
    if (!safeProjectId) return;
    ensureCredits().catch(() => {});
  }, [ensureCredits, safeProjectId]);

  const persistPayload = async (nextPayload) => {
    if (!safeProjectId) return null;
    const saved = await saveDraft({ projectId: safeProjectId, payload: nextPayload });
    setDraftPayload(storefrontToPayload(saved, project?.title || nextPayload.title));
    return saved;
  };

  const handleGenerate = async () => {
    if (!safeProjectId) return;
    setBusy('generate');
    setError('');
    setMessage('');
    try {
      const generated = await generateStorefront({ projectId: safeProjectId });
      const saved = await persistPayload(generated);
      setMessage(saved?.status === 'published' ? 'Storefront regenerated. Publish again to update the live page.' : 'Storefront generated.');
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Could not generate storefront.');
    } finally {
      setBusy('');
    }
  };

  const handleRevise = async () => {
    if (!safeProjectId || !instruction.trim()) return;
    setBusy('revise');
    setError('');
    setMessage('');
    try {
      const revised = await reviseStorefront({
        projectId: safeProjectId,
        currentPayload: payload,
        instruction,
      });
      await persistPayload(revised);
      setInstruction('');
      setMessage('AI changes applied.');
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Could not revise storefront.');
    } finally {
      setBusy('');
    }
  };

  const handleGenerateCoverPackage = async () => {
    if (!safeProjectId) return;
    setBusy('covers');
    setError('');
    setMessage('');
    try {
      const nextPayload = await generateCoverPackage({
        projectId: safeProjectId,
        currentPayload: payload,
      });
      await persistPayload(nextPayload);
      setMessage('Front cover, back cover, and hero book mockup generated.');
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Could not generate cover package.');
    } finally {
      setBusy('');
    }
  };

  const updateField = (field, value) => {
    setDraftPayload((current) => ({ ...payload, ...current, [field]: value }));
  };

  const updateHero = (field, value) => {
    const sections = [...(payload.sections || [])];
    const heroIndex = Math.max(0, sections.findIndex((section) => section.type === 'hero'));
    const hero = sections[heroIndex] || { id: 'hero', type: 'hero' };
    sections[heroIndex] = { ...hero, [field]: value };
    setDraftPayload((current) => ({ ...payload, ...current, sections }));
  };

  const handleSaveManual = async () => {
    setBusy('save');
    setError('');
    try {
      await persistPayload(payload);
      setMessage('Draft saved.');
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Could not save storefront.');
    } finally {
      setBusy('');
    }
  };

  const handlePublish = async () => {
    if (!safeProjectId) return;
    setBusy('publish');
    setError('');
    try {
      await persistPayload(payload);
      const published = await publish({ projectId: safeProjectId });
      setDraftPayload(storefrontToPayload(published, project?.title || payload.title));
      setMessage('Storefront published.');
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Could not publish storefront.');
    } finally {
      setBusy('');
    }
  };

  const handleUnpublish = async () => {
    if (!safeProjectId) return;
    setBusy('unpublish');
    setError('');
    try {
      const updated = await unpublish({ projectId: safeProjectId });
      setDraftPayload(storefrontToPayload(updated, project?.title || payload.title));
      setMessage('Storefront unpublished.');
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Could not unpublish storefront.');
    } finally {
      setBusy('');
    }
  };

  if (!safeProjectId) {
    return <main className="storefront-studio loading">Invalid project link.</main>;
  }

  if (project === undefined || storefront === undefined) {
    return <main className="storefront-studio loading">Loading Storefront Studio...</main>;
  }

  if (!project) {
    return <main className="storefront-studio loading">Project not found.</main>;
  }

  const hero = payload.sections?.find((section) => section.type === 'hero') || {};
  const isPublished = storefront?.status === 'published';

  return (
    <main className="storefront-studio">
      <header className="ss-header">
        <button className="ss-back" onClick={() => navigate(`/projects/${safeProjectId}`)}>← Project</button>
        <div>
          <p className="ss-eyebrow">Storefront Studio</p>
          <h1>{project.title}</h1>
        </div>
        <div className="ss-header-actions">
          {isPublished && publicPath ? <Link className="btn btn-secondary" to={publicPath}>View live</Link> : null}
          <button className="btn btn-primary" onClick={handlePublish} disabled={!!busy}>{busy === 'publish' ? 'Publishing...' : 'Publish'}</button>
        </div>
      </header>

      {error && <div className="ss-alert error">{error}</div>}
      {message && <div className="ss-alert success">{message}</div>}

      <section className="ss-layout">
        <aside className="ss-panel">
          <div className="ss-credit-card">
            <div>
              <span>Credits remaining</span>
              <strong>{creditStatus === undefined ? '...' : Number(totalCredits).toFixed(1)}</strong>
            </div>
            <p>Storefront: ~{GENERATE_CREDIT_ESTIMATE} · AI change: ~{REVISE_CREDIT_ESTIMATE} · Cover package: ~{COVER_PACKAGE_CREDIT_ESTIMATE}</p>
          </div>

          <button className="ss-generate" onClick={handleGenerate} disabled={!!busy || !hasGenerateCredits}>
            {busy === 'generate' ? 'Generating...' : storefront ? `Regenerate storefront (~${GENERATE_CREDIT_ESTIMATE})` : `Generate storefront (~${GENERATE_CREDIT_ESTIMATE})`}
          </button>
          {!hasGenerateCredits && <p className="ss-credit-warning">Not enough credits to generate a storefront.</p>}

          <button className="ss-cover-package" onClick={handleGenerateCoverPackage} disabled={!!busy || !hasCoverPackageCredits}>
            {busy === 'covers' ? 'Generating covers...' : `Generate front, back & mockup (~${COVER_PACKAGE_CREDIT_ESTIMATE})`}
          </button>
          <p className="ss-helper-text">Creates a front cover, back cover, and physical book hero image from the current storefront copy.</p>
          {!hasCoverPackageCredits && <p className="ss-credit-warning">Not enough credits for the cover package.</p>}

          <div className="ss-control">
            <label>Ask AI for changes</label>
            <textarea
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder="Make it more cinematic, shorten the hero, add a stronger reader hook..."
              rows={4}
            />
            <button className="btn btn-secondary" onClick={handleRevise} disabled={!!busy || !instruction.trim() || !hasReviseCredits}>
              {busy === 'revise' ? 'Applying...' : `Apply AI change (~${REVISE_CREDIT_ESTIMATE})`}
            </button>
            {!hasReviseCredits && <p className="ss-credit-warning">Not enough credits for AI changes.</p>}
          </div>

          <div className="ss-control">
            <label>Book title</label>
            <input value={payload.title || ''} onChange={(event) => updateField('title', event.target.value)} />
          </div>
          <div className="ss-control">
            <label>Hero headline</label>
            <input value={hero.title || ''} onChange={(event) => updateHero('title', event.target.value)} />
          </div>
          <div className="ss-control">
            <label>Hero copy</label>
            <textarea rows={4} value={hero.body || ''} onChange={(event) => updateHero('body', event.target.value)} />
          </div>
          <div className="ss-control">
            <label>CTA label</label>
            <input value={payload.ctaLabel || ''} onChange={(event) => updateField('ctaLabel', event.target.value)} />
          </div>
          <div className="ss-control">
            <label>Theme</label>
            <div className="ss-theme-grid">
              {THEME_OPTIONS.map((theme) => (
                <button
                  key={theme}
                  className={payload.theme === theme ? 'active' : ''}
                  onClick={() => updateField('theme', theme)}
                >
                  {theme}
                </button>
              ))}
            </div>
          </div>

          <div className="ss-actions">
            <button className="btn btn-secondary" onClick={handleSaveManual} disabled={!!busy}>{busy === 'save' ? 'Saving...' : 'Save draft'}</button>
            {isPublished && <button className="btn btn-secondary" onClick={handleUnpublish} disabled={!!busy}>Unpublish</button>}
          </div>

          {publicUrl && (
            <div className="ss-public-link">
              <span>Public link</span>
              <code>{publicUrl}</code>
              <button onClick={() => navigator.clipboard?.writeText(publicUrl)}>Copy</button>
            </div>
          )}
        </aside>

        <section className="ss-preview">
          <StorefrontPreview payload={payload} statusLabel={isPublished ? 'Published' : 'Draft preview'} />
        </section>
      </section>
    </main>
  );
}
